import path from 'node:path';
import { packageManagerEnv } from '../utils/spawn-env.js';
import fs from 'node:fs';
import net from 'node:net';
import readline from 'node:readline';
import { spawn } from 'node:child_process';
import { fileURLToPath } from 'node:url';
import { logger } from '../utils/logger.js';

interface InitChatOptions {
  yes?: boolean;
  framework?: 'vite' | 'nextjs';
}

type Framework = 'vite' | 'nextjs';
type PackageManager = 'npm' | 'pnpm' | 'yarn' | 'bun';

/**
 * Get the @standardagents/react version to use based on the CLI's version.
 * If CLI is running a prerelease (e.g., 0.11.0-next.1), use that tag.
 * Otherwise, use the exact CLI version.
 */
function getReactPackageVersion(): string {
  try {
    const __dirname = path.dirname(fileURLToPath(import.meta.url));
    // dist/index.js -> ../package.json
    const pkgPath = path.resolve(__dirname, '../package.json');
    const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'));
    const version = pkg.version as string;

    const prereleaseMatch = version.match(/-([a-z]+)\./i);
    if (prereleaseMatch) {
      return prereleaseMatch[1];
    }

    return `^${version}`;
  } catch {
    return 'latest';
  }
}

/**
 * Get the path to the chat source directory bundled with the CLI
 */
function getChatSourceDir(): string {
  const __dirname = path.dirname(fileURLToPath(import.meta.url));
  // dist/index.js -> ../chat
  return path.resolve(__dirname, '../chat');
}

async function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

async function promptPassword(question: string): Promise<string> {
  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    stdout.write(question);

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    let password = '';

    const redraw = () => {
      stdout.clearLine(0);
      stdout.cursorTo(0);
      stdout.write(question + '*'.repeat(password.length));
    };

    const onData = (data: string) => {
      const str = data.toString();

      for (let i = 0; i < str.length; i++) {
        const c = str[i];

        if (c === '\x1B') {
          if (str[i + 1] === '[') {
            i += 2;
            continue;
          }
          continue;
        }

        switch (c) {
          case '\n':
          case '\r':
          case '\u0004':
            stdin.setRawMode(wasRaw ?? false);
            stdin.pause();
            stdin.removeListener('data', onData);
            stdout.write('\n');
            resolve(password);
            return;
          case '\u0003':
            stdout.write('\n');
            process.exit(1);
            break;
          case '\u007F':
          case '\b':
            if (password.length > 0) {
              password = password.slice(0, -1);
              redraw();
            }
            break;
          default:
            if (c.charCodeAt(0) >= 32) {
              password += c;
              stdout.write('*');
            }
            break;
        }
      }
    };

    stdin.on('data', onData);
  });
}

function runCommand(command: string, args: string[], cwd: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      cwd,
      stdio: 'inherit',
      shell: false,
      // npx-inherited npm_config_allow_scripts fatals npm 12 project installs
      // (EALLOWSCRIPTS); approvals live in the generated package.json instead.
      env: packageManagerEnv(),
    });

    child.on('close', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`Command failed with exit code ${code}`));
      }
    });

    child.on('error', reject);
  });
}

function detectPackageManager(): PackageManager {
  const userAgent = process.env.npm_config_user_agent;
  if (userAgent) {
    if (userAgent.includes('pnpm')) return 'pnpm';
    if (userAgent.includes('yarn')) return 'yarn';
    if (userAgent.includes('bun')) return 'bun';
  }

  if (fs.existsSync('pnpm-lock.yaml')) return 'pnpm';
  if (fs.existsSync('yarn.lock')) return 'yarn';
  if (fs.existsSync('bun.lockb')) return 'bun';
  if (fs.existsSync('package-lock.json')) return 'npm';

  return 'npm';
}

async function selectPackageManager(detected: PackageManager): Promise<PackageManager> {
  const options: PackageManager[] = ['npm', 'pnpm', 'yarn', 'bun'];
  const detectedIndex = options.indexOf(detected);

  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    let selectedIndex = detectedIndex;

    const renderOptions = () => {
      stdout.write('\x1B[?25l');
      stdout.write(`\x1B[${options.length + 1}A`);
      stdout.write('\x1B[0J');

      stdout.write('Select a package manager:\n');
      options.forEach((opt, i) => {
        const marker = i === detectedIndex ? ' (detected)' : '';
        const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
        const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
        stdout.write(`${prefix} ${highlight}${opt}${marker}\x1B[0m\n`);
      });
    };

    stdout.write('Select a package manager:\n');
    options.forEach((opt, i) => {
      const marker = i === detectedIndex ? ' (detected)' : '';
      const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
      const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
      stdout.write(`${prefix} ${highlight}${opt}${marker}\x1B[0m\n`);
    });

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    const cleanup = () => {
      stdin.setRawMode(wasRaw ?? false);
      stdin.pause();
      stdin.removeListener('data', onData);
      stdout.write('\x1B[?25h');
    };

    const onData = (key: string) => {
      if (key === '\x1B[A' || key === 'k') {
        selectedIndex = (selectedIndex - 1 + options.length) % options.length;
        renderOptions();
      } else if (key === '\x1B[B' || key === 'j') {
        selectedIndex = (selectedIndex + 1) % options.length;
        renderOptions();
      } else if (key === '\r' || key === '\n') {
        cleanup();
        resolve(options[selectedIndex]);
      } else if (key === '\x03') {
        cleanup();
        stdout.write('\n');
        process.exit(1);
      }
    };

    stdin.on('data', onData);
  });
}

async function selectFramework(): Promise<Framework> {
  const options: { value: Framework; label: string }[] = [
    { value: 'vite', label: 'Vite (React + TypeScript)' },
    { value: 'nextjs', label: 'Next.js (App Router)' },
  ];

  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    let selectedIndex = 0;

    const renderOptions = () => {
      stdout.write('\x1B[?25l');
      stdout.write(`\x1B[${options.length + 1}A`);
      stdout.write('\x1B[0J');

      stdout.write('Select a framework:\n');
      options.forEach((opt, i) => {
        const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
        const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
        stdout.write(`${prefix} ${highlight}${opt.label}\x1B[0m\n`);
      });
    };

    stdout.write('Select a framework:\n');
    options.forEach((opt, i) => {
      const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
      const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
      stdout.write(`${prefix} ${highlight}${opt.label}\x1B[0m\n`);
    });

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    const cleanup = () => {
      stdin.setRawMode(wasRaw ?? false);
      stdin.pause();
      stdin.removeListener('data', onData);
      stdout.write('\x1B[?25h');
    };

    const onData = (key: string) => {
      if (key === '\x1B[A' || key === 'k') {
        selectedIndex = (selectedIndex - 1 + options.length) % options.length;
        renderOptions();
      } else if (key === '\x1B[B' || key === 'j') {
        selectedIndex = (selectedIndex + 1) % options.length;
        renderOptions();
      } else if (key === '\r' || key === '\n') {
        cleanup();
        resolve(options[selectedIndex].value);
      } else if (key === '\x03') {
        cleanup();
        stdout.write('\n');
        process.exit(1);
      }
    };

    stdin.on('data', onData);
  });
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

async function isPortOpen(port: number): Promise<boolean> {
  const tryConnect = (host: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const socket = new net.Socket();
      socket.setTimeout(200);

      socket.on('connect', () => {
        socket.destroy();
        resolve(true);
      });

      socket.on('timeout', () => {
        socket.destroy();
        resolve(false);
      });

      socket.on('error', () => {
        socket.destroy();
        resolve(false);
      });

      socket.connect(port, host);
    });
  };

  if (await tryConnect('127.0.0.1')) return true;
  if (await tryConnect('::1')) return true;
  return false;
}

async function isAgentbuilderInstance(url: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1000);

    const response = await fetch(`${url}/api/agents`, {
      signal: controller.signal,
      headers: { 'Accept': 'application/json' },
    });

    clearTimeout(timeout);

    if (response.status === 401) {
      return true;
    }

    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.agents);
    }
    return false;
  } catch {
    return false;
  }
}

async function detectAgentbuilderInstance(): Promise<string | null> {
  const portsToScan = [
    5173, 5174, 5175, 5176, 5177, 5178, 5179, 5180,
    3000, 3001, 3002, 3003,
    8080, 8000, 4000,
  ];

  logger.log('\x1B[90mScanning for running agentbuilder instance...\x1B[0m');

  const openPorts: number[] = [];

  await Promise.all(
    portsToScan.map(async (port) => {
      if (await isPortOpen(port)) {
        openPorts.push(port);
      }
    })
  );

  for (const port of openPorts.sort((a, b) => a - b)) {
    const url = `http://localhost:${port}`;
    if (await isAgentbuilderInstance(url)) {
      return url;
    }
  }

  return null;
}

/**
 * Dependencies of the scaffolded chat that need their install scripts
 * (esbuild via Vite; sharp via Next.js). pnpm 10+ skips unapproved scripts
 * and pnpm 11 fails the install outright with ERR_PNPM_IGNORED_BUILDS; npm 12
 * skips them silently. Mirrors the project template's approval surfaces.
 */
export const CHAT_BUILD_SCRIPT_DEPENDENCIES = ['esbuild', 'sharp'] as const;

interface ChatPackageJson {
  pnpm?: { onlyBuiltDependencies?: string[] };
  allowScripts?: Record<string, boolean>;
  [key: string]: unknown;
}

/** package.json approvals: pnpm < 11 and npm 12. */
export function applyChatBuildApprovals<T extends ChatPackageJson>(
  pkg: T,
): T & { pnpm: { onlyBuiltDependencies: string[] }; allowScripts: Record<string, boolean> } {
  return {
    ...pkg,
    pnpm: { ...pkg.pnpm, onlyBuiltDependencies: [...CHAT_BUILD_SCRIPT_DEPENDENCIES] },
    allowScripts: Object.fromEntries(CHAT_BUILD_SCRIPT_DEPENDENCIES.map((name) => [name, true])),
  };
}

/**
 * pnpm-workspace.yaml approvals: pnpm 11 reads only this file (allowBuilds),
 * pnpm 10.4+ reads onlyBuiltDependencies. First-party packages are exempt from
 * pnpm 11's minimum release age so same-day releases install.
 */
export function renderChatPnpmWorkspaceYaml(): string {
  const list = CHAT_BUILD_SCRIPT_DEPENDENCIES.map((name) => `  - "${name}"`).join('\n');
  const map = CHAT_BUILD_SCRIPT_DEPENDENCIES.map((name) => `  "${name}": true`).join('\n');
  return `onlyBuiltDependencies:\n${list}\nallowBuilds:\n${map}\nminimumReleaseAgeExclude:\n  - "@standardagents/*"\n`;
}

/**
 * Copy a directory recursively, skipping certain files/folders. The chat's
 * own unit tests stay in the template package and are never scaffolded.
 */
function copyDir(src: string, dest: string, skip: string[] = []) {
  if (!fs.existsSync(dest)) {
    fs.mkdirSync(dest, { recursive: true });
  }

  const entries = fs.readdirSync(src, { withFileTypes: true });

  for (const entry of entries) {
    if (skip.includes(entry.name) || /\.test\.tsx?$/.test(entry.name)) continue;

    const srcPath = path.join(src, entry.name);
    const destPath = path.join(dest, entry.name);

    if (entry.isDirectory()) {
      copyDir(srcPath, destPath, skip);
    } else {
      fs.copyFileSync(srcPath, destPath);
    }
  }
}

/**
 * Update package.json with correct SDK version and project name
 */
function updatePackageJson(filePath: string, projectName: string, reactVersion: string) {
  const pkg = JSON.parse(fs.readFileSync(filePath, 'utf-8'));

  // Update name
  pkg.name = projectName;

  // Update @standardagents/react version
  if (pkg.dependencies?.['@standardagents/react']) {
    pkg.dependencies['@standardagents/react'] = reactVersion;
  }

  // Remove private flag for user projects
  delete pkg.private;

  // Remove workspace-specific scripts
  delete pkg.scripts?.['dev:next'];
  delete pkg.scripts?.['build:next'];

  fs.writeFileSync(filePath, JSON.stringify(pkg, null, 2) + '\n', 'utf-8');
}

/**
 * Update package.json for Next.js project
 */
function updateNextPackageJson(filePath: string, projectName: string, reactVersion: string) {
  const pkg = JSON.parse(fs.readFileSync(filePath, 'utf-8'));

  // Update name
  pkg.name = projectName;

  // Update @standardagents/react version
  if (pkg.dependencies?.['@standardagents/react']) {
    pkg.dependencies['@standardagents/react'] = reactVersion;
  }

  // Remove private flag for user projects
  delete pkg.private;

  // Update scripts for Next.js only
  pkg.scripts = {
    dev: 'next dev',
    build: 'next build',
    start: 'next start',
    lint: 'next lint',
  };

  // Remove vite-specific deps
  delete pkg.devDependencies?.['@tailwindcss/vite'];
  delete pkg.devDependencies?.['@vitejs/plugin-react'];
  delete pkg.devDependencies?.['vite'];

  fs.writeFileSync(filePath, JSON.stringify(pkg, null, 2) + '\n', 'utf-8');
}

export async function initChat(projectNameArg?: string, options: InitChatOptions = {}) {
  const cwd = process.cwd();
  const chatSourceDir = getChatSourceDir();

  // Check if chat source exists
  if (!fs.existsSync(chatSourceDir)) {
    logger.error('Chat source files not found. This CLI installation may be corrupted.');
    process.exit(1);
  }

  logger.log('');
  logger.info('Scaffolding a frontend chat application...');
  logger.log('');

  // Get project name
  let projectName = projectNameArg;

  if (!projectName && !options.yes) {
    projectName = await prompt('Project name: ');
  }

  if (!projectName) {
    logger.error('Project name is required');
    process.exit(1);
  }

  const projectPath = path.join(cwd, projectName);

  // Check if directory already exists
  if (fs.existsSync(projectPath)) {
    logger.error(`Directory "${projectName}" already exists`);
    process.exit(1);
  }

  // Step 1: Select framework
  let framework: Framework;
  if (options.framework) {
    framework = options.framework;
  } else if (options.yes) {
    framework = 'vite';
  } else {
    framework = await selectFramework();
    logger.log('');
  }

  // Step 2: Get Agentbuilder URL
  let serverUrl: string;
  if (options.yes) {
    serverUrl = 'http://localhost:5173';
  } else {
    const detectedUrl = await detectAgentbuilderInstance();

    if (detectedUrl) {
      logger.success(`Found agentbuilder at ${detectedUrl}`);
      const urlInput = await prompt(`Agentbuilder dev server URL [${detectedUrl}]: `);
      serverUrl = urlInput || detectedUrl;
    } else {
      logger.log('\x1B[90mNo running agentbuilder instance found\x1B[0m');
      const urlInput = await prompt('Agentbuilder dev server URL [http://localhost:5173]: ');
      serverUrl = urlInput || 'http://localhost:5173';
    }

    if (!isValidUrl(serverUrl)) {
      logger.error('Invalid URL format');
      process.exit(1);
    }
  }

  // Select package manager
  const detectedPm = detectPackageManager();
  let pm: PackageManager;
  if (options.yes) {
    pm = detectedPm;
  } else {
    pm = await selectPackageManager(detectedPm);
    logger.log('');
  }

  logger.log('');
  logger.info(`Creating ${framework === 'vite' ? 'Vite' : 'Next.js'} project...`);
  logger.log('');

  // Create project directory
  fs.mkdirSync(projectPath, { recursive: true });

  // Get React package version
  const reactVersion = getReactPackageVersion();

  // Copy source files based on framework
  if (framework === 'vite') {
    await scaffoldVite(projectPath, chatSourceDir, projectName, serverUrl, reactVersion);
  } else {
    await scaffoldNextjs(projectPath, chatSourceDir, projectName, serverUrl, reactVersion);
  }

  // Install dependencies
  logger.log('');
  logger.info('Installing dependencies...');
  logger.log('');

  try {
    await runCommand(pm, pm === 'npm' ? ['install'] : ['install'], projectPath);
  } catch (error) {
    logger.error('Failed to install dependencies');
    process.exit(1);
  }

  // Success message
  logger.log('');
  logger.success('Chat UI scaffolded successfully!');
  logger.log('');

  // Start dev server
  logger.info('Starting development server...');
  logger.log('');

  const devArgs = pm === 'npm' ? ['run', 'dev'] : ['dev'];
  const devProcess = spawn(pm, devArgs, {
    cwd: projectPath,
    stdio: ['inherit', 'pipe', 'pipe'],
    shell: false,
  });

  let browserOpened = false;
  const openBrowser = (url: string) => {
    if (browserOpened) return;
    browserOpened = true;
    const openCmd = process.platform === 'darwin' ? 'open' :
                    process.platform === 'win32' ? 'start' : 'xdg-open';
    spawn(openCmd, [url], { stdio: 'ignore', detached: true }).unref();
  };

  devProcess.stdout?.on('data', (data: Buffer) => {
    const text = data.toString();
    process.stdout.write(data);

    const urlMatch = text.match(/Local:\s+(https?:\/\/localhost:\d+\/?)/);
    if (urlMatch && !browserOpened) {
      openBrowser(urlMatch[1]);
    }
  });

  devProcess.stderr?.on('data', (data: Buffer) => {
    process.stderr.write(data);
  });

  devProcess.on('error', (error) => {
    logger.error(`Failed to start dev server: ${error.message}`);
    process.exit(1);
  });
}

async function scaffoldVite(
  projectPath: string,
  chatSourceDir: string,
  projectName: string,
  serverUrl: string,
  reactVersion: string
) {
  // Copy shared source files
  copyDir(path.join(chatSourceDir, 'src'), path.join(projectPath, 'src'), []);
  logger.success('Copied src/');

  // Copy vite-specific files to root
  const viteDir = path.join(chatSourceDir, 'vite');

  // Copy and update index.html to point to src/main.tsx
  let indexHtml = fs.readFileSync(path.join(viteDir, 'index.html'), 'utf-8');
  indexHtml = indexHtml.replace('src="/main.tsx"', 'src="/src/main.tsx"');
  fs.writeFileSync(path.join(projectPath, 'index.html'), indexHtml, 'utf-8');
  logger.success('Created index.html');

  fs.copyFileSync(path.join(viteDir, 'main.tsx'), path.join(projectPath, 'src', 'main.tsx'));
  logger.success('Created src/main.tsx');

  // Standalone vite.config.ts. Local AgentBuilder instances reject
  // cross-origin requests, so the dev server proxies /api (HTTP + WebSocket)
  // to VITE_AGENTBUILDER_URL and the chat stays same-origin.
  const viteConfig = `import { defineConfig, loadEnv } from 'vite'
import react from '@vitejs/plugin-react'
import tailwindcss from '@tailwindcss/vite'

export default defineConfig(({ mode }) => {
  const env = { ...loadEnv(mode, process.cwd(), ''), ...process.env }
  return {
    plugins: [
      react(),
      tailwindcss(),
    ],
    server: {
      proxy: {
        '/api': {
          target: env.VITE_AGENTBUILDER_URL || 'http://localhost:5173',
          ws: true,
          // Keep the browser's Host so the runtime sees a same-origin request.
          changeOrigin: false,
        },
      },
    },
  }
})
`;
  fs.writeFileSync(path.join(projectPath, 'vite.config.ts'), viteConfig, 'utf-8');
  logger.success('Created vite.config.ts');

  // Copy and update tsconfig
  fs.copyFileSync(path.join(chatSourceDir, 'tsconfig.json'), path.join(projectPath, 'tsconfig.json'));
  // Update tsconfig to remove vite-specific include
  const tsconfig = JSON.parse(fs.readFileSync(path.join(projectPath, 'tsconfig.json'), 'utf-8'));
  tsconfig.include = ['src'];
  delete tsconfig.compilerOptions?.paths;
  fs.writeFileSync(path.join(projectPath, 'tsconfig.json'), JSON.stringify(tsconfig, null, 2) + '\n', 'utf-8');
  logger.success('Created tsconfig.json');

  // Copy and update package.json
  fs.copyFileSync(path.join(chatSourceDir, 'package.json'), path.join(projectPath, 'package.json'));
  // Read, modify, and write package.json
  const pkg = JSON.parse(fs.readFileSync(path.join(projectPath, 'package.json'), 'utf-8'));
  pkg.name = projectName;
  pkg.scripts = {
    dev: 'vite',
    build: 'vite build',
    preview: 'vite preview',
  };
  delete pkg.private;
  // Update @standardagents/react version
  if (pkg.dependencies?.['@standardagents/react']) {
    pkg.dependencies['@standardagents/react'] = reactVersion;
  }
  // Remove next.js deps
  delete pkg.dependencies?.['next'];
  delete pkg.devDependencies?.['@tailwindcss/postcss'];
  delete pkg.devDependencies?.['postcss'];
  delete pkg.devDependencies?.['vitest'];
  fs.writeFileSync(path.join(projectPath, 'package.json'), JSON.stringify(applyChatBuildApprovals(pkg), null, 2) + '\n', 'utf-8');
  fs.writeFileSync(path.join(projectPath, 'pnpm-workspace.yaml'), renderChatPnpmWorkspaceYaml(), 'utf-8');
  logger.success('Created package.json');

  // Create .env
  const envContent = `# Agentbuilder connection
VITE_AGENTBUILDER_URL=${serverUrl}
`;
  fs.writeFileSync(path.join(projectPath, '.env'), envContent, 'utf-8');
  logger.success('Created .env');

  // Create .gitignore
  fs.writeFileSync(path.join(projectPath, '.gitignore'), `node_modules
dist
.env
`, 'utf-8');
  logger.success('Created .gitignore');

  // Update main.tsx paths (it expects ../src/ but now they're in same dir)
  let mainTsx = fs.readFileSync(path.join(projectPath, 'src', 'main.tsx'), 'utf-8');
  mainTsx = mainTsx
    .replace(/from '\.\.\/src\//g, "from './")
    .replace(/import '\.\.\/src\//g, "import './");
  fs.writeFileSync(path.join(projectPath, 'src', 'main.tsx'), mainTsx, 'utf-8');

  // Copy favicon
  if (fs.existsSync(path.join(viteDir, 'favicon.svg'))) {
    fs.copyFileSync(path.join(viteDir, 'favicon.svg'), path.join(projectPath, 'favicon.svg'));
    logger.success('Created favicon.svg');
  }
}

async function scaffoldNextjs(
  projectPath: string,
  chatSourceDir: string,
  projectName: string,
  serverUrl: string,
  reactVersion: string
) {
  // Copy shared source files to src/
  copyDir(path.join(chatSourceDir, 'src'), path.join(projectPath, 'src'), []);
  logger.success('Copied src/');

  // Copy next-specific files
  const nextDir = path.join(chatSourceDir, 'next');

  // Copy app directory
  copyDir(path.join(nextDir, 'app'), path.join(projectPath, 'app'), []);
  logger.success('Copied app/');

  // Copy next.config.ts
  fs.copyFileSync(path.join(nextDir, 'next.config.ts'), path.join(projectPath, 'next.config.ts'));
  logger.success('Created next.config.ts');

  // Copy postcss.config.mjs
  fs.copyFileSync(path.join(nextDir, 'postcss.config.mjs'), path.join(projectPath, 'postcss.config.mjs'));
  logger.success('Created postcss.config.mjs');

  // Create tsconfig.json for Next.js
  const tsconfig = {
    compilerOptions: {
      target: "ES2017",
      lib: ["dom", "dom.iterable", "esnext"],
      allowJs: true,
      skipLibCheck: true,
      strict: true,
      noEmit: true,
      esModuleInterop: true,
      module: "esnext",
      moduleResolution: "bundler",
      resolveJsonModule: true,
      isolatedModules: true,
      jsx: "preserve",
      incremental: true,
      plugins: [{ name: "next" }],
      paths: {
        "@/*": ["./src/*"]
      }
    },
    include: ["next-env.d.ts", "**/*.ts", "**/*.tsx", ".next/types/**/*.ts"],
    exclude: ["node_modules"]
  };
  fs.writeFileSync(path.join(projectPath, 'tsconfig.json'), JSON.stringify(tsconfig, null, 2) + '\n', 'utf-8');
  logger.success('Created tsconfig.json');

  // Create package.json for Next.js
  const pkg = JSON.parse(fs.readFileSync(path.join(chatSourceDir, 'package.json'), 'utf-8'));
  pkg.name = projectName;
  pkg.scripts = {
    dev: 'next dev',
    build: 'next build',
    start: 'next start',
  };
  delete pkg.private;
  // Update @standardagents/react version
  if (pkg.dependencies?.['@standardagents/react']) {
    pkg.dependencies['@standardagents/react'] = reactVersion;
  }
  // Remove vite-specific deps
  delete pkg.devDependencies?.['@tailwindcss/vite'];
  delete pkg.devDependencies?.['@vitejs/plugin-react'];
  delete pkg.devDependencies?.['vite'];
  delete pkg.devDependencies?.['vitest'];
  fs.writeFileSync(path.join(projectPath, 'package.json'), JSON.stringify(applyChatBuildApprovals(pkg), null, 2) + '\n', 'utf-8');
  fs.writeFileSync(path.join(projectPath, 'pnpm-workspace.yaml'), renderChatPnpmWorkspaceYaml(), 'utf-8');
  logger.success('Created package.json');

  // Create .env.local
  const envContent = `# Agentbuilder connection
NEXT_PUBLIC_AGENTBUILDER_URL=${serverUrl}
`;
  fs.writeFileSync(path.join(projectPath, '.env.local'), envContent, 'utf-8');
  logger.success('Created .env.local');

  // Create .gitignore
  fs.writeFileSync(path.join(projectPath, '.gitignore'), `node_modules
.next
out
.env.local
`, 'utf-8');
  logger.success('Created .gitignore');

  // Update layout.tsx paths
  let layoutTsx = fs.readFileSync(path.join(projectPath, 'app', 'layout.tsx'), 'utf-8');
  layoutTsx = layoutTsx.replace(/from '\.\.\/\.\.\/src\//g, "from '../src/");
  fs.writeFileSync(path.join(projectPath, 'app', 'layout.tsx'), layoutTsx, 'utf-8');

  // Update page.tsx paths
  let pageTsx = fs.readFileSync(path.join(projectPath, 'app', 'page.tsx'), 'utf-8');
  pageTsx = pageTsx.replace(/from '\.\.\/\.\.\/src\//g, "from '../src/");
  fs.writeFileSync(path.join(projectPath, 'app', 'page.tsx'), pageTsx, 'utf-8');
}
